import type { ReactNode } from 'react';
import type { ReviewKind } from '../../../shared/progress';
import Wordmark from '../Wordmark';

// Shared "review page" chrome for the tour demo scenes — utility bar, hero,
// and the three-action review card — reused by suggest-edits (pages A and C)
// and general-notes (page A). Geometry (fixed heights/margins in px) is
// deliberate: cursor coordinates in src/client/lib/tourScenes.ts assume this
// exact layout on the 1100x688 canvas.

export interface ReviewCardState {
  /** Action 2 reads "Resume editing" with the gold number, instead of
   *  "Suggest edits" with the teal one. */
  resumeMode?: boolean;
  /** Highlight action 2 (suggest edits / resume editing) with the "lit" ring. */
  suggestLit?: boolean;
  /** Highlight action 3 (general notes) with the "lit" ring. */
  notesLit?: boolean;
}

interface Props {
  card: ReviewCardState;
  /** Swaps the hero for the interview brief's. Everything else, including
   *  every fixed height above the doc, is identical for both kinds so the
   *  measured cursor coordinates hold either way. */
  kind: ReviewKind;
  /** Gold notice shown under the review card (suggest-edits page C only). */
  notice?: string | null;
  /** The doc (or whatever comes after the review card) for this page. */
  children?: ReactNode;
}

export default function ReviewPageChrome({ card, kind, notice, children }: Props) {
  const brief = kind === 'brief';
  return (
    <div className="absolute inset-0 bg-page">
      <div className="h-14 flex items-center justify-between px-9 border-b border-line bg-card">
        <Wordmark size="sm" />
        <span className="flex items-center gap-3">
          <span className="font-mono text-[11px] text-faint">Contact Henry</span>
          <span className="font-mono text-[11px] text-faint">Download PDF</span>
        </span>
      </div>
      <div className="h-[100px] px-9 pt-[18px] border-b border-line">
        <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted">
          Prepared for <span className="text-fg">{brief ? 'Marlow & Finch Physio' : 'Aurora Coffee Roasters'}</span>
        </p>
        <h1 className="font-display font-bold text-[28px] tracking-[-0.015em] text-fg mt-1">
          {brief ? "Patient stories: Dana's recovery" : 'Brand film, sixty seconds'}
        </h1>
      </div>
      <div className="mx-9 mt-[18px] bg-card border border-accent rounded-md px-6 py-3">
        <div className="text-center text-[13px] text-muted pb-2.5 border-b border-line">
          <span className="font-mono text-[10px] uppercase tracking-[0.06em] font-bold text-accent mr-1">
            Round 2
          </span>
          · Reply by Fri 24 Jul.
        </div>
        <div className="grid grid-cols-3 pt-1">
          <ReviewAction n={1} label="Comment" />
          <ReviewAction
            n={2}
            label={card.resumeMode ? 'Resume editing' : 'Suggest edits'}
            gold={card.resumeMode}
            lit={card.suggestLit}
          />
          <ReviewAction n={3} label="General notes" lit={card.notesLit} />
        </div>
        <div className="flex justify-center pt-2 mt-0.5 border-t border-line">
          <span className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-accent text-accent-fg font-semibold text-[12px]">
            Send for review
          </span>
        </div>
      </div>
      {notice && (
        <div className="mx-9 mt-2.5 border-l-[3px] border-l-gold bg-gold/10 rounded-r-sm px-3.5 py-2 text-[12px] text-gold">
          {notice}
        </div>
      )}
      {children}
    </div>
  );
}

function ReviewAction({ n, label, gold, lit }: { n: number; label: string; gold?: boolean; lit?: boolean }) {
  return (
    <div
      className={`flex flex-col items-center gap-0.5 py-2.5 rounded-lg transition-colors duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        lit ? 'ring-2 ring-accent bg-accent/5' : ''
      }`}
    >
      <span
        className={`inline-flex items-center justify-center w-[22px] h-[22px] rounded-full font-mono text-[11px] font-bold text-accent-fg ${
          gold ? 'bg-gold' : 'bg-accent'
        }`}
      >
        {n}
      </span>
      <span className="text-[13px] font-semibold text-fg">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Doc rows — the audio/visual two-column layout shared by every scene.
// ---------------------------------------------------------------------------

export interface DocRow {
  audio: ReactNode;
  visual: string;
  editing?: boolean;
}

export function DocPanel({ rows, top }: { rows: DocRow[]; top: number }) {
  return (
    <div
      className="absolute left-9 right-9 bg-card border border-line rounded-md shadow-flare px-5 py-5"
      style={{ top }}
    >
      <div className="grid grid-cols-[1.25fr_1fr] gap-2.5 mb-1.5">
        <span className="font-mono text-[10px] font-semibold tracking-[0.14em] uppercase text-faint px-3.5 pb-1.5 border-b border-line">
          Audio
        </span>
        <span className="font-mono text-[10px] font-semibold tracking-[0.14em] uppercase text-accent px-3.5 pb-1.5 border-b border-line">
          Visual
        </span>
      </div>
      {rows.map((row, i) => (
        <div key={i} className="grid grid-cols-[1.25fr_1fr] gap-2.5 my-2">
          <div
            className={`relative px-4 py-3.5 rounded-sm text-[15px] leading-[1.5] text-fg border transition-colors duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              row.editing ? 'bg-card border-accent/50' : 'bg-elevated border-transparent'
            }`}
          >
            {row.audio}
          </div>
          <div className="relative pl-8 px-4 py-3.5 rounded-sm text-[14px] leading-[1.5] text-muted bg-elevated border border-transparent">
            <span className="absolute top-3.5 left-3 font-mono text-[9px] font-semibold text-accent" aria-hidden>
              V
            </span>
            {row.visual}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Brief sections: what the same doc looks like on an interview brief. Titled
// sections of prose, no AUDIO / VISUAL columns. Mirrors the client-facing
// field styling in styles.css ([data-brief-view="client"] .brief-field__title,
// display serif 21px/600, and the 22px gap + hairline of .brief-field +
// .brief-field), so the tour and the real review page rhyme.
// ---------------------------------------------------------------------------

/** The two sections the brief scenes share, so pages A, B and C of one scene
 *  (and the other scenes) can never drift apart on the copy. */
export const BRIEF_PURPOSE_TITLE = 'Video purpose & messaging';
export const BRIEF_PURPOSE_BODY =
  "Marlow & Finch want a short film that shows what recovery actually looks like from the patient's side, not the clinic's. It carries the first impression for anyone deciding whether physio is worth booking at all.";
export const BRIEF_AUDIENCE_TITLE = 'Audience';
export const BRIEF_AUDIENCE_BODY =
  'Adults around Brunswick and Coburg who have been putting up with a nagging injury for months. They are cautious about the cost, and about being sold twelve sessions they did not ask for.';

export interface BriefSection {
  title: string;
  /** Body paragraphs. Mutually exclusive with `bullets`. */
  paragraphs?: ReactNode[];
  /** Body as a bullet list, e.g. Key messages. */
  bullets?: ReactNode[];
  /** Draws the body as suggest mode's editable block, the way DocPanel draws
   *  an editing cell. The padding is cancelled by matching negative margins
   *  so the box grows outwards and the prose never shifts when it appears. */
  editing?: boolean;
  /** `data-tour-target` on the paragraph block, so the dev harness can measure
   *  it (preview-scenes.html, src/dev/previewScenes.tsx). Harmless in production. */
  target?: string;
}

export function BriefSections({ sections }: { sections: BriefSection[] }) {
  return (
    <>
      {sections.map((section, i) => (
        <div key={i} className={i > 0 ? 'mt-[22px] pt-[22px] border-t border-line' : ''}>
          <h3 className="font-display font-semibold text-[21px] tracking-[-0.01em] leading-[1.25] text-fg mb-2.5">
            {section.title}
          </h3>
          {section.bullets ? (
            <ul className="list-disc pl-5 text-[15px] leading-[1.5] text-fg marker:text-faint">
              {section.bullets.map((bullet, j) => (
                <li key={j} className={j > 0 ? 'mt-1.5' : ''}>
                  {bullet}
                </li>
              ))}
            </ul>
          ) : (
            <div
              data-tour-target={section.target}
              className={`-mx-4 -my-2 px-4 py-2 rounded-sm border transition-colors duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                section.editing ? 'bg-card border-accent/50' : 'border-transparent'
              }`}
            >
              {(section.paragraphs ?? []).map((paragraph, j) => (
                <p key={j} className={`text-[15px] leading-[1.5] text-fg ${j > 0 ? 'mt-2.5' : ''}`}>
                  {paragraph}
                </p>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

export function BriefDocPanel({ sections, top }: { sections: BriefSection[]; top: number }) {
  return (
    <div
      className="absolute left-9 right-9 bg-card border border-line rounded-md shadow-flare px-5 py-5"
      style={{ top }}
    >
      <BriefSections sections={sections} />
    </div>
  );
}
